// 003 (FR-019a; research R8; data-model §9): Use as a parent too = enableParentRole → Refresh access
// (refreshSession) → the renewed session must hold PARENT → web switches to parent mode. Anything
// else is a failure shown with Retry; nothing changes for the user.
import { useCallback, useContext, useState } from "react";

import { type AppError, isAppError, toAppError } from "@/api/errors";
import { useSessionController } from "@/session/hooks";
import { usePlatform } from "@/session/platform";
import { SessionContext } from "@/session/SessionProvider";
import { servedRoles } from "@/session/store";

import { useJoinClient } from "./joinApi";

export type ParentRoleOfferState =
  | { status: "idle" }
  | { status: "pending" }
  | { status: "error"; error: AppError };

/** A renewed session without PARENT reads as a forbidden-like local error. */
const NO_PARENT: AppError = {
  kind: "forbidden",
  status: null,
  code: null,
  requestId: null,
  fieldErrors: [],
  retryAfterSeconds: null,
  allowedClients: null,
};

export function useEnableParentRole(): {
  state: ParentRoleOfferState;
  run: () => Promise<"parent" | "failed">;
} {
  const client = useJoinClient();
  const store = useContext(SessionContext);
  const controller = useSessionController();
  const platform = usePlatform();
  const [state, setState] = useState<ParentRoleOfferState>({ status: "idle" });

  const run = useCallback(async (): Promise<"parent" | "failed"> => {
    setState({ status: "pending" });
    try {
      await client.enableParentRole();
    } catch (thrown) {
      setState({
        status: "error",
        error: isAppError(thrown)
          ? thrown
          : toAppError({ thrown, timedOut: false }),
      });
      return "failed";
    }
    await controller.refreshAccess();
    const session = store.getSnapshot();
    if (
      session.status !== "signedIn" ||
      !servedRoles(session.roles).includes("PARENT")
    ) {
      setState({ status: "error", error: NO_PARENT });
      return "failed";
    }
    if (platform === "web") await controller.switchRole("PARENT");
    setState({ status: "idle" });
    return "parent";
  }, [client, controller, platform, store]);

  return { state, run };
}
